import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The ways this store can be paid.
 *
 * One *payment option* is a tender the customer can pick — cash at the till,
 * a card through Stripe, ATH Móvil, an EBT card — and each says where it may
 * be used: the web front, the POS, self-checkout, the deli counter, delivery.
 * A store with nothing set up here cannot take money at all, which is why
 * there is a one-press way to lay down a sensible default set.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `PaymentProvider` — who processes it. */
export const PROVIDERS = [
  'Stripe',
  'PayPal',
  'Evertec',
  'Accepta',
  'MCS',
  'MMM',
  'Icon',
  'Store',
  'Venmo',
  'SSS',
  'Humana',
  'MagicPay',
] as const;

/** `PaymentTypeSelector` — what the customer is holding. */
export const SELECTORS = [
  'Credit',
  'EBT',
  'Debit',
  'Pin Debit',
  'Pre-Paid',
  'Gift Card',
  'Benefits Card',
  'ACH',
  'P2B',
  'CheckOut',
] as const;

/** `PaymentChargeType` — when and how the money is taken. */
export const CHARGE_TYPES = [
  'upFront',
  'dispatch',
  'purchaseOrder',
  'appToApp',
  'billingAgreement',
  'qrCode',
  'terminal',
] as const;

/** `PaymentType` — the tender itself. Twenty-six of them, in the API's order. */
export const PAYMENT_TYPES = [
  'Stripe',
  'ATH Móvil',
  'Cash',
  'Other',
  'EBT',
  'PayPal',
  'Cheque',
  'ATH',
  'Vanilla Card',
  'Gift Card',
  'MCS',
  'MMM',
  'Accepta',
  'Net 30',
  'Net 60',
  'Not selected',
  'Credit Card',
  'Debit Card',
  'SSS',
  'Humana',
  'BrainTree',
  'inStore',
  'Prepaid',
  'EBT Cash',
  'Unica',
  'Fondo',
] as const;

/** `PaymentOptionDto`. */
export interface PaymentOption {
  id: string;
  locationId: string | null;
  name: string | null;
  imageUrl: string | null;
  provider: number;
  selector: number;
  chargeType: number;
  paymentType: number;
  minCharge: number;
  isActive: boolean;
  offlineOnly: boolean;
  taxExcluded: boolean;
  ignoreOrderCap: boolean;
  allowNonFoodProducts: boolean;
  allowAlcoholicItems: boolean;
  allowTransfers: boolean;
  allowsServiceFeePayment: boolean;
  allowedInRegularService: boolean;
  allowedInDeliveryService: boolean;
  allowedInDeliService: boolean;
  allowedInPosService: boolean;
  allowedInSelfCheckoutService: boolean;
  needsBillingAgreement: boolean;
  hasActiveBillingAgreement: boolean;
  hasFreeDelivery: boolean;
  shouldOpenCashDrawer: boolean;
  publicToken: string | null;
  privateToken: string | null;
  transferToken: string | null;
  specialInstrctionsEng: string | null;
  specialInstrctionsSpa: string | null;
}

/**
 * `PaymentOptionBindingModel`.
 *
 * The same shape minus `id`, which travels in the path. Note the server's
 * spelling of `specialInstrctions` — it is missing the second `u`, in both the
 * DTO and the model, so it is kept as-is rather than quietly corrected.
 */
export type PaymentOptionInput = Omit<PaymentOption, 'id'>;

export function toPaymentInput(option: PaymentOption): PaymentOptionInput {
  // The id identifies the row in the path, so the body must not repeat it.
  const input = { ...option } as Partial<PaymentOption>;
  delete input.id;
  return input as PaymentOptionInput;
}

/**
 * A new option seeded from an existing one.
 *
 * Two things deliberately do not travel. `isActive` starts off, so a copy
 * cannot go live before somebody has looked at it — everything else about it
 * is a duplicate of a tender that customers already see. And
 * `hasActiveBillingAgreement` is the server's own state about an agreement
 * that was struck with the *original*; a new row has none.
 */
export function copyOfPayment(
  source: PaymentOption,
  locationId: string,
  name: string
): PaymentOptionInput {
  return {
    ...toPaymentInput(source),
    locationId,
    name,
    isActive: false,
    hasActiveBillingAgreement: false,
  };
}

/** What a new option starts as — the defaults the original's Create used. */
export function blankPayment(locationId: string): PaymentOptionInput {
  return {
    locationId,
    name: '',
    imageUrl: null,
    provider: 0,
    selector: 0,
    chargeType: 0,
    paymentType: 3,
    minCharge: 0,
    isActive: false,
    offlineOnly: false,
    taxExcluded: false,
    ignoreOrderCap: false,
    allowNonFoodProducts: false,
    allowAlcoholicItems: false,
    allowTransfers: false,
    allowsServiceFeePayment: false,
    allowedInRegularService: false,
    allowedInDeliveryService: false,
    allowedInDeliService: false,
    allowedInPosService: false,
    allowedInSelfCheckoutService: false,
    needsBillingAgreement: false,
    hasActiveBillingAgreement: false,
    hasFreeDelivery: false,
    shouldOpenCashDrawer: false,
    publicToken: null,
    privateToken: null,
    transferToken: null,
    specialInstrctionsEng: null,
    specialInstrctionsSpa: null,
  };
}

export function usePaymentOptions(locationId: string | null) {
  return useQuery({
    queryKey: ['payment-options', locationId],
    queryFn: () =>
      get<{ results: PaymentOption[] }>(`admin/paymentoptions/${locationId}`),
    select: (data) =>
      [...(data.results ?? [])].sort(
        (a, b) =>
          Number(b.isActive) - Number(a.isActive)
          || (a.name ?? '').localeCompare(b.name ?? '')
      ),
    enabled: !!locationId,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['payment-options'] });
}

export function useCreatePaymentOption() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: PaymentOptionInput) =>
      unwrap<PaymentOption>(api.post('businesses/paymentoptions', input)),
    onSuccess: invalidate,
  });
}

/** Keyed by the option's id in the path; the body carries no id. */
export function useUpdatePaymentOption() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PaymentOptionInput }) =>
      unwrap<PaymentOption>(
        api.put(`businesses/updatepaymentoptions/${id}`, input)
      ),
    onSuccess: invalidate,
  });
}

export function useDeletePaymentOption() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.post(`admin/delete/paymentoptions/${id}`, {})),
    onSuccess: invalidate,
  });
}

/**
 * `CreateDefaultPaymentOptionsBindingModel` — lay down a starting set.
 *
 * Keyed by the *business*, not the location, unlike everything else here.
 */
export interface DefaultPaymentsInput {
  businessId: string;
  webfrontCash: boolean;
  webfrontPayPal: boolean;
  webfrontPayPalBillingAgreement: boolean;
  webfrontAth: boolean;
  webfrontEbt: boolean;
  webfrontTripleS: boolean;
  webfrontHumana: boolean;
  webfrontMCS: boolean;
  webfrontMMM: boolean;
  posTerminalCash: boolean;
  posTerminalCashOffline: boolean;
  posTerminalCreditDebit: boolean;
  posTerminalEbt: boolean;
  posTerminalEbtCash: boolean;
  posTerminalFondo: boolean;
  posTerminalUnica: boolean;
  selfCheckoutTerminalCreditDebit: boolean;
  selfCheckoutTerminalEbt: boolean;
}

/** The three groups the defaults dialog offers, in the model's own order. */
export const DEFAULT_GROUPS = {
  webfront: [
    'webfrontCash',
    'webfrontPayPal',
    'webfrontPayPalBillingAgreement',
    'webfrontAth',
    'webfrontEbt',
    'webfrontTripleS',
    'webfrontHumana',
    'webfrontMCS',
    'webfrontMMM',
  ],
  pos: [
    'posTerminalCash',
    'posTerminalCashOffline',
    'posTerminalCreditDebit',
    'posTerminalEbt',
    'posTerminalEbtCash',
    'posTerminalFondo',
    'posTerminalUnica',
  ],
  selfCheckout: [
    'selfCheckoutTerminalCreditDebit',
    'selfCheckoutTerminalEbt',
  ],
} as const satisfies Record<string, readonly (keyof DefaultPaymentsInput)[]>;

export function useCreateDefaultPayments() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: DefaultPaymentsInput) =>
      unwrap<unknown>(api.post('admin/create/paymentoptions/default', input)),
    onSuccess: invalidate,
  });
}

/** Payment option images go to their own container. */
export function useUploadPaymentImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('utility/paymentoptions/uploadfile', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
